import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import type { Board, Employee, Task } from "@/types/db";
import { plainError } from "@/lib/plain-error";
import { objectiveOptionLabel, needsPriorityLink, type LinkableObjective } from "@/lib/priority-link";
import { usePermission } from "@/hooks/usePermission";
import { canPermanentlyDelete, personDeletePatch, UNARCHIVE_PATCH } from "@/lib/board/archive";
import { CloseButton } from "@/components/CloseButton";
import { useConfirm } from "@/components/useConfirm";
import { TaskAttachments } from "@/components/TaskAttachments";
import { RichTextEditor, AutoText, sanitizeHtml, htmlToText } from "@/components/RichText";

/** Comments written before rich text are plain; open them in the editor with their line breaks. */
const commentToEditorHtml = (s: string) =>
  /<[a-z][\s\S]*>/i.test(s) ? s : s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>");

export function TaskDetailModal({
  taskId, employees, boardMemberIds, assigneesByTask, columns, board, objectives, onClose, onChanged,
}: {
  taskId: string;
  employees: Pick<Employee, "id" | "first_name" | "last_name" | "email">[];
  /** Who can actually open this board — see loadBoardMembers(). */
  boardMemberIds: Set<string>;
  assigneesByTask: Record<string, string[]>;
  columns: { id: string; name: string; is_done?: boolean; kind?: string | null }[];
  board: Pick<Board, "requires_priority_link"> | null;
  objectives: LinkableObjective[];
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const [task, setTask] = useState<Task | null>(null);
  const [assigneeSearch, setAssigneeSearch] = useState("");
  const [comments, setComments] = useState<{ id: string; employee_id: string; content: string; created_at: string; edited_at?: string | null }[]>([]);
  const [newComment, setNewComment] = useState("");
  const [composerKey, setComposerKey] = useState(0);
  const [editingComment, setEditingComment] = useState<{ id: string; text: string } | null>(null);
  const [commentError, setCommentError] = useState<string | null>(null);
  const { ask: askConfirm, dialog: confirmDialog } = useConfirm();
  const { employee } = useAuth();
  const [assignError, setAssignError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const canManagePriorities = usePermission("hq.priority.manage");
  const [newObjOpen, setNewObjOpen] = useState(false);
  const [newObjPriority, setNewObjPriority] = useState<number>(3);
  const [newObjDeadline, setNewObjDeadline] = useState("");
  const [objError, setObjError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const { data } = await supabase.from("hq_tasks").select("*").eq("id", taskId).maybeSingle();
      setTask(data as Task);
      const { data: c } = await supabase.from("hq_task_comments").select("*").eq("task_id", taskId).order("created_at");
      setComments(c ?? []);
    })();
  }, [taskId]);

  if (!task) return null;

  async function update(patch: Partial<Task>): Promise<boolean> {
    const previous = task!;
    setTask({ ...previous, ...patch });
    setSaveError(null);
    const { error } = await supabase.from("hq_tasks").update(patch).eq("id", previous.id);
    if (error) { setTask(previous); setSaveError(plainError(error, "save that change")); return false; }
    await onChanged();
    return true;
  }

  // HQ-BOARD-001. Assigning someone who cannot open the board used to succeed
  // and then be invisible to them: the task showed as assigned to whoever did
  // it and returned nothing on the assignee's own reads. The database refuses
  // that now, so the refusal has to be shown rather than swallowed.
  async function toggleAssignee(empId: string) {
    setAssignError(null);
    const current = assigneesByTask[task!.id] || [];
    const { error } = current.includes(empId)
      ? await supabase.from("hq_task_assignees").delete().eq("task_id", task!.id).eq("employee_id", empId)
      : await supabase.from("hq_task_assignees").insert({ task_id: task!.id, employee_id: empId });
    if (error) { setAssignError(plainError(error, "change that assignment")); return; }
    await onChanged();
  }

  /** Give someone access to this board, then assign them. */
  async function grantAndAssign(empId: string) {
    if (!task) return;
    setAssignError(null);
    const { error } = await supabase.from("hq_board_members")
      .insert({ board_id: task.board_id, employee_id: empId });
    if (error) { setAssignError(plainError(error, "add them to this board")); return; }
    await onChanged();
    await toggleAssignee(empId);
  }

  async function postComment() {
    // A comment is rich text (HTML). An empty editor still holds markup like <br>, so judge by its text.
    if (!htmlToText(newComment) || !employee) return;
    const c = sanitizeHtml(newComment).trim();
    setNewComment("");
    setComposerKey(k => k + 1); // a fresh, empty editor
    const { data } = await supabase.from("hq_task_comments")
      .insert({ task_id: task!.id, employee_id: employee.id, content: c })
      .select().single();
    if (data) setComments(prev => [...prev, data]);
  }

  // Edit and delete are for the person who wrote the comment; the database enforces it too.
  async function saveCommentEdit() {
    if (!editingComment) return;
    const text = sanitizeHtml(editingComment.text).trim();
    if (!htmlToText(editingComment.text)) { setCommentError("A comment cannot be empty. Delete it instead."); return; }
    setCommentError(null);
    const editedAt = new Date().toISOString();
    const { data, error: err } = await supabase.from("hq_task_comments")
      .update({ content: text, edited_at: editedAt }).eq("id", editingComment.id).select().maybeSingle();
    if (err || !data) { setCommentError(err ? plainError(err) : "That comment could not be changed."); return; }
    setComments(prev => prev.map(c => (c.id === data.id ? { ...c, content: data.content, edited_at: data.edited_at } : c)));
    setEditingComment(null);
  }

  async function deleteComment(id: string) {
    const ok = await askConfirm({ title: "Delete this comment?", body: "It is removed from the card for everyone.", confirmLabel: "Delete comment", danger: true });
    if (!ok) return;
    setCommentError(null);
    const { error: err } = await supabase.from("hq_task_comments").delete().eq("id", id);
    if (err) { setCommentError(plainError(err)); return; }
    setComments(prev => prev.filter(c => c.id !== id));
  }

  /**
   * A new company priority, born from this card.
   *
   * The card is the WHAT; the priority is the commitment — a rank and a
   * deadline the whole company can see. Creating one needs the same
   * permission the Company Priorities page requires (the database enforces
   * it regardless), and the card is linked the moment it exists.
   */
  async function createPriorityFromCard() {
    if (!task || !employee) return;
    setObjError(null);
    if (!newObjDeadline) { setObjError("A company priority needs a deadline."); return; }
    const today = new Date().toISOString().slice(0, 10);
    const { data: obj, error } = await supabase.from("hq_pe_objectives").insert({
      title: task.title,
      description: task.description,
      priority: newObjPriority,
      start_date: today,
      deadline: newObjDeadline,
      status: "active",
      owner_id: employee.id,
      created_by: employee.id,
    }).select("id").single();
    if (error || !obj) { setObjError(plainError(error, "create the priority")); return; }
    setNewObjOpen(false);
    await update({ pe_objective_id: obj.id });
  }

  async function del() {
    if (!employee) return;
    const ok = await askConfirm({
      title: "Delete this card?",
      body: "It leaves the board and goes to Archived. You can put it back. It is not erased.",
      confirmLabel: "Delete card",
      danger: true,
    });
    if (!ok) return;
    if (await update(personDeletePatch(employee.id, new Date().toISOString()))) onClose();
  }

  async function deletePermanently() {
    const ok = await askConfirm({
      title: "Delete this card permanently?",
      body: "The card and its comments are removed. This cannot be undone. A record of the title, number and board is kept.",
      confirmLabel: "Delete permanently",
      danger: true,
    });
    if (!ok) return;
    setSaveError(null);
    const { error } = await supabase.rpc("hq_task_delete_permanently", { p_task_id: task!.id });
    if (error) { setSaveError(plainError(error, "delete this card permanently")); return; }
    await onChanged();
    onClose();
  }

  const currentAssignees = assigneesByTask[task.id] || [];
  const searchText = assigneeSearch.trim().toLowerCase();
  const matchesSearch = (e: Pick<Employee, "first_name" | "last_name" | "email">) =>
    !searchText || `${e.first_name ?? ""} ${e.last_name ?? ""} ${e.email ?? ""}`.toLowerCase().includes(searchText);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-xl bg-white p-6 shadow-soft"
        onClick={e => e.stopPropagation()}
        onPaste={e => {
          // Pasting a screenshot straight into a card is how most of them get
          // here, and it has to keep working now the upload lives in
          // TaskAttachments. The event carries the file to whichever card is open.
          const img = Array.from(e.clipboardData?.items ?? []).find(it => it.type.startsWith("image/"));
          const f = img?.getAsFile();
          if (f) {
            e.preventDefault();
            window.dispatchEvent(new CustomEvent("hq:attach-file", { detail: { taskId, file: f } }));
          }
        }}
      >
        <div className="mb-4 flex items-start justify-between">
          <input
            aria-label="Card title"
            className="w-full bg-transparent text-2xl font-bold focus:outline-none"
            value={task.title}
            onChange={e => setTask({ ...task, title: e.target.value })}
            onBlur={() => update({ title: task.title })}
          />
          <CloseButton onClick={onClose} className="text-gray2 hover:text-ink" />
        </div>

        {saveError && <div role="alert" className="mb-3 rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad">{saveError}</div>}
        {task.archived_at && (
          <div className="mb-4 rounded-lg bg-offwhite px-3 py-3 text-sm ring-1 ring-line">
            <p>This card is archived, so it is off the board. The card and its notes are still here.</p>
            <p className="mt-1 text-gray2">
              Putting it back leaves it in its column. If that column is Complete, next Sunday night
              the weekly archive will take it off the board again.
            </p>
            <button
              type="button"
              className="btn btn-sm mt-3 min-h-11"
              onClick={() => void update({ ...UNARCHIVE_PATCH })}
            >
              Put back on the board
            </button>
          </div>
        )}
        {confirmDialog}
        <div className="grid gap-4 sm:grid-cols-[1fr_220px]">
          <div className="space-y-4">
            <div>
              <label className="label">Description</label>
              <textarea
                aria-label="Description"
                className="input min-h-[100px]"
                value={task.description ?? ""}
                onChange={e => setTask({ ...task, description: e.target.value })}
                onBlur={() => update({ description: task.description })}
                placeholder="Add a description…"
              />
            </div>

            {/* Three kinds, because work has three shapes: the screenshot of
                the thing that is wrong, the file somebody produced, and the
                Drive document everybody is actually working out of. */}
            <TaskAttachments taskId={taskId} employeeId={employee?.id} />

            <div>
              <label className="label">Comments</label>
              <div className="space-y-2">
                {comments.map(c => (
                  <div key={c.id} className="rounded-lg bg-offwhite p-3 text-sm">
                    <div className="text-xs text-gray2">{employees.find(e => e.id === c.employee_id)?.first_name || "Someone"} · {new Date(c.created_at).toLocaleString()}</div>
                    {editingComment?.id === c.id ? (
                      <div className="mt-1 space-y-2">
                        <RichTextEditor
                          value={editingComment.text}
                          onChange={html => setEditingComment(cur => (cur ? { id: cur.id, text: html } : cur))}
                          minHeight={80}
                        />
                        <div className="flex gap-2">
                          <button type="button" onClick={() => void saveCommentEdit()} className="btn">Save</button>
                          <button type="button" onClick={() => { setEditingComment(null); setCommentError(null); }} className="btn btn-ghost">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="mt-1"><AutoText text={c.content} />{c.edited_at && <span className="text-xs text-gray2">(edited)</span>}</div>
                        {c.employee_id === employee?.id && (
                          <div className="mt-1 flex gap-3 text-xs">
                            <button type="button" onClick={() => { setCommentError(null); setEditingComment({ id: c.id, text: commentToEditorHtml(c.content) }); }} className="text-teal-dark hover:underline">Edit</button>
                            <button type="button" onClick={() => void deleteComment(c.id)} className="text-bad hover:underline">Delete</button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
                <div className="flex items-start gap-2">
                  {/* Rich text, not a one-line input: pasting a list into an input flattens it into
                      one paragraph. Formatting, bullets, numbers and pasted lines are kept. */}
                  <div className="min-w-0 flex-1">
                    <RichTextEditor
                      key={composerKey}
                      value={newComment}
                      onChange={setNewComment}
                      minHeight={60}
                      placeholder="Add a comment…"
                    />
                  </div>
                  <button onClick={postComment} className="btn">Post</button>
                </div>
                {commentError && <div role="alert" className="rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad">{commentError}</div>}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {/* ── THE TOP ARROW OF THE CASCADE ─────────────────────────────
                Card → company priority → the priority's task list with
                deadlines → each assignee's My Day, ranked by the priority.
                Everything below the link already exists in the Priority
                Engine; this control is the link. */}
            <div>
              <label className="label">Company priority</label>
              {needsPriorityLink(board, task) && (
                <div className="mb-1.5 rounded-lg bg-warn-bg px-2.5 py-1.5 text-[11px] text-warn">
                  Cards on this board link to a company priority — pick the one
                  this work serves.
                </div>
              )}
              <select
                className="input"
                aria-label="Company priority"
                value={task.pe_objective_id ?? ""}
                onChange={e => update({ pe_objective_id: e.target.value || null })}
              >
                <option value="">None</option>
                {objectives.map(o => (
                  <option key={o.id} value={o.id}>{objectiveOptionLabel(o)}</option>
                ))}
              </select>
              {task.pe_objective_id && (
                <Link
                  to={`/priority/objective/${task.pe_objective_id}`}
                  className="mt-1 inline-block text-xs font-medium text-teal-dark hover:underline"
                >
                  Open the priority &amp; its task list →
                </Link>
              )}
              {canManagePriorities && !task.pe_objective_id && (
                newObjOpen ? (
                  <div className="mt-2 space-y-2 rounded-lg bg-offwhite p-2.5 ring-1 ring-line">
                    <div className="text-[11px] text-gray2">
                      New priority from this card — it takes the card's title, and
                      its task list is where the activities and deadlines go.
                    </div>
                    <select className="input" aria-label="New priority rank" value={newObjPriority} onChange={e => setNewObjPriority(Number(e.target.value))}>
                      {[1, 2, 3, 4, 5].map(p => <option key={p} value={p}>P{p}</option>)}
                    </select>
                    <input type="date" className="input" aria-label="New priority deadline" value={newObjDeadline} onChange={e => setNewObjDeadline(e.target.value)} />
                    {objError && <div className="rounded bg-bad-bg px-2 py-1 text-[11px] text-bad">{objError}</div>}
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setNewObjOpen(false)} className="text-xs text-gray2 hover:text-bad">Cancel</button>
                      <button onClick={() => void createPriorityFromCard()} className="btn btn-sm">Create &amp; link</button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setNewObjOpen(true)}
                    className="mt-1 text-xs font-medium text-teal-dark hover:underline"
                  >
                    + New company priority from this card
                  </button>
                )
              )}
            </div>
            <div>
              <label className="label">Column</label>
              <select className="input" aria-label="Column" value={task.column_id} onChange={e => {
                const column_id = e.target.value;
                const dest = columns.find(c => c.id === column_id);
                const patch: Partial<Task> = { column_id };
                if (task.archived_at && dest && dest.kind !== "complete") {
                  Object.assign(patch, UNARCHIVE_PATCH);
                }
                void update(patch);
              }}>
                {columns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Priority</label>
              <select className="input" aria-label="Priority" value={task.priority} onChange={e => update({ priority: e.target.value as Task["priority"] })}>
                <option value="low">Low</option>
                <option value="med">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="label">Due date</label>
              <input type="date" className="input" aria-label="Due date" value={task.due_date ?? ""} onChange={e => update({ due_date: e.target.value || null })} />
            </div>
            <div>
              <label className="label">Assignees</label>
              {assignError && (
                <div className="mb-2 rounded-lg bg-bad-bg px-3 py-2 text-xs text-bad">{assignError}</div>
              )}
              <input
                type="search"
                className="input mb-1"
                aria-label="Search people to assign"
                placeholder="Search a name…"
                value={assigneeSearch}
                onChange={e => setAssigneeSearch(e.target.value)}
              />
              <div className="space-y-1 max-h-40 overflow-auto rounded ring-1 ring-line p-2">
                {assigneeSearch.trim() && !employees.some(matchesSearch) && (
                  <p className="text-xs text-gray2">No one matches “{assigneeSearch.trim()}”.</p>
                )}
                {employees.filter(e => boardMemberIds.has(e.id) && matchesSearch(e)).map(e => (
                  <label key={e.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" aria-label={`Assign ${e.first_name || e.email} ${e.last_name ?? ""}`} checked={currentAssignees.includes(e.id)} onChange={() => toggleAssignee(e.id)} />
                    <span>{e.first_name || e.email} {e.last_name ?? ""}</span>
                  </label>
                ))}
                {/* Everyone else is listed too, but assigning them is not a
                    silent no-op — it is an explicit decision to share the board,
                    which is the step that was missing entirely. */}
                {employees.filter(e => !boardMemberIds.has(e.id) && matchesSearch(e)).length > 0 && (
                  <div className="mt-2 border-t border-line pt-2">
                    <div className="mb-1 text-[11px] uppercase tracking-wider text-gray2">
                      Cannot open this board
                    </div>
                    {employees.filter(e => !boardMemberIds.has(e.id) && matchesSearch(e)).map(e => (
                      <div key={e.id} className="flex items-center justify-between gap-2 py-0.5 text-sm">
                        <span className="truncate text-gray2">{e.first_name || e.email} {e.last_name ?? ""}</span>
                        <button
                          onClick={() => void grantAndAssign(e.id)}
                          className="shrink-0 text-xs font-medium text-teal-dark hover:underline"
                          title="Add them to this board, then assign the task"
                        >Add &amp; assign</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {!task.archived_at && (
              <button type="button" onClick={() => void del()} className="btn btn-ghost text-bad ring-bad min-h-11 w-full">
                Delete card
              </button>
            )}
            {canPermanentlyDelete(employee?.role, task) && (
              <button type="button" onClick={() => void deletePermanently()} className="btn btn-ghost text-bad ring-bad min-h-11 w-full">
                Delete permanently
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
